import { z } from "zod";

import { periodSchema } from "./common";

/** 计数类指标：`change` 是与上一等长周期的差值。 */
const countMetricSchema = z.object({
  value: z.int().nonnegative(),
  change: z.int().nullable(),
});

/**
 * 比率类指标，取值 0 到 1；分母（已完成归属判定的会话）为 0 时值为 null。
 * `change` 是百分点差，以小数表示，例如 0.03 表示上升 3 个百分点。
 */
const rateMetricSchema = z.object({
  value: z.number().min(0).max(1).nullable(),
  change: z.number().min(-1).max(1).nullable(),
});

export const homeMetricsQuerySchema = z.object({
  period: periodSchema.default("last_7_days"),
});

export type HomeMetricsQuery = z.infer<typeof homeMetricsQuerySchema>;

/**
 * `GET /api/home/metrics` 的响应（PRD 4.2，按 GON-11 砍掉 AI-Assisted Orders）。
 * 三张指标卡顺序固定；趋势按日，两条线是会话数与 AI 解决数。
 * 数据来自 AI 后端的 Tenant-scoped Conversation；Human backstop 按窗口内有 Handoff（AI 回复的 `transfer_reason` 非空）的会话去重计数，与 Inbox 的转人工标记同一口径，不看邮件是否受理发送。
 * `emptyReason` 非 null 时页面显示对应空态：还没有知识，或店面尚未上线；
 * 有知识且已上线但没有会话，由 `conversations.value === 0` 判断。
 */
export const homeMetricsResponseSchema = z.object({
  period: periodSchema,
  metrics: z.object({
    conversations: countMetricSchema,
    aiResolutionRate: rateMetricSchema,
    humanBackstopRate: rateMetricSchema,
  }),
  trend: z.array(
    z.object({
      date: z.iso.date(),
      conversations: z.int().nonnegative(),
      aiResolved: z.int().nonnegative(),
    }),
  ),
  emptyReason: z.enum(["no_knowledge", "storefront_not_live"]).nullable(),
});

export type HomeMetricsResponse = z.infer<typeof homeMetricsResponseSchema>;
